<template>
  <v-dialog max-width="1100px" v-model="showDialog" persistent>
    <v-card>
      <!-- Header -->
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-file-check-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Confirm DA</span>
        <v-spacer></v-spacer>
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Order Info Strip -->
      <div
        class="d-flex align-center flex-wrap px-5 py-2"
        style="background: #eef2fb; border-bottom: 1px solid #d5ddf0; gap: 20px"
      >
        <div v-if="orderInfo.eToken" class="info-item d-flex align-center">
          <v-icon x-small color="primary" class="mr-1">mdi-identifier</v-icon>
          <span class="info-label mr-1">Token:</span>
          <span class="info-value">{{ orderInfo.eToken }}</span>
        </div>
        <div
          v-if="orderInfo.sourceLocation"
          class="info-item d-flex align-center"
        >
          <v-icon x-small color="primary" class="mr-1"
            >mdi-map-marker-outline</v-icon
          >
          <span class="info-label mr-1">Source:</span>
          <span class="info-value">{{ orderInfo.sourceLocation }}</span>
        </div>
        <div
          v-if="orderInfo.customerName"
          class="info-item d-flex align-center"
        >
          <v-icon x-small color="primary" class="mr-1"
            >mdi-account-outline</v-icon
          >
          <span class="info-label mr-1">Customer:</span>
          <span class="info-value">{{ orderInfo.customerName }}</span>
        </div>
        <div v-if="orderInfo.isOog || orderInfo.isHazardous || orderInfo.isReefer" class="d-flex align-center" style="gap:6px">
          <span v-if="orderInfo.isOog" style="background:#FFE0B2;color:#E65100;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700;letter-spacing:.04em">OOG</span>
          <span v-if="orderInfo.isHazardous" style="background:#FFCDD2;color:#B71C1C;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700;letter-spacing:.04em">HAZARDOUS</span>
          <span v-if="orderInfo.isReefer" style="background:#E1F5FE;color:#0277BD;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700;letter-spacing:.04em">REEFER</span>
        </div>
      </div>

      <v-card-text class="px-6 pt-5 pb-2" style="min-height: 420px">
        <!-- Contract Status — Vendor (sub-hire only) -->
        <div
          v-if="contractChecked && form.is_sub_hire && !hasVendorContract"
          class="contract-status contract-status--warn mb-3"
        >
          <v-icon small class="mr-2">mdi-alert-outline</v-icon>
          <span>No active transporter contract found for this vendor.</span>
        </div>
        <div
          v-if="contractChecked && form.is_sub_hire && hasVendorContract"
          class="contract-status contract-status--found mb-3"
        >
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-check-circle-outline</v-icon>
            <span class="font-weight-medium"
              >Transporter contract — {{ vendorContractIdDisplay }}</span
            >
          </div>
          <div
            v-if="vendorRates.length"
            class="d-flex flex-wrap mt-2"
            style="gap: 6px"
          >
            <span v-for="(r, i) in vendorRates" :key="i" class="rate-chip">
              {{ r.container_type_name }}: AED {{ r.rate }}
            </span>
          </div>
        </div>

        <!-- Containers Section — Customer & Lane per container -->
        <div class="section-block mt-2">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1">mdi-package-variant</v-icon>
            <span class="text-caption font-weight-bold text-uppercase primary--text">
              Actual Containers <span class="grey--text">(Max 4)</span>
            </span>
            <v-spacer />
            <v-btn v-if="form.containers.length < 4" text x-small color="primary" @click="addContainer">
              <v-icon x-small class="mr-1">mdi-plus-circle-outline</v-icon>
              Add Container
            </v-btn>
          </div>

          <div v-for="(container, index) in form.containers" :key="index" class="container-row mb-3 pa-3">
            <div class="d-flex align-center mb-2">
              <v-avatar size="20" color="primary lighten-4" class="mr-2 flex-shrink-0">
                <span class="text-caption primary--text font-weight-bold">{{ index + 1 }}</span>
              </v-avatar>
              <v-text-field
                dense outlined hide-details="auto" label="Container No"
                v-model="container.container_no" class="mr-2" style="max-width:200px"
              />
              <MultiSelect
                label="Container Type"
                :items="containerTypeList" itemText="type_name" itemValue="id"
                hide-details="auto" outlined dense clearable
                v-model="container.container_type"
                class="mr-2" style="min-width:200px"
                @input="onContractFieldChange"
              />
              <v-btn v-if="form.containers.length > 1" icon x-small color="error" @click="removeContainer(index)">
                <v-icon small>mdi-close-circle</v-icon>
              </v-btn>
            </div>
            <v-row dense>
              <v-col cols="6">
                <Autocomplete
                  label="Customer *"
                  :items="customerList" itemText="company_name" itemValue="id"
                  :loading="customerLoading" :totalItems="customerCount"
                  @updateList="fetchCustomers" hide-details="auto"
                  :value="container.customer || form.customer"
                  @input="(val) => onContainerCustomerChange(index, val)"
                />
              </v-col>
              <v-col cols="6">
                <Autocomplete
                  label="Destination Lane"
                  :items="laneLists[index] || laneList" itemText="lane_title" itemValue="id"
                  :loading="laneLoading" :totalItems="laneCount"
                  @updateList="fetchLanes" hide-details="auto"
                  :value="container.lane || form.lane"
                  @input="(val) => onContainerLaneChange(index, val)"
                />
              </v-col>
            </v-row>
            <!-- Per-container contract status -->
            <div v-if="containerContracts[index]" class="mt-2">
              <div v-if="containerContracts[index].checked && containerContracts[index].found"
                class="contract-status contract-status--found">
                <div class="d-flex align-center">
                  <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>
                  <span class="font-weight-medium">Contract — {{ containerContracts[index].contractId }}</span>
                </div>
                <div v-if="containerContracts[index].rate" class="mt-1">
                  <span class="rate-chip">{{ container._typeNameHint || '' }}: AED {{ containerContracts[index].rate }}</span>
                </div>
              </div>
              <div v-if="containerContracts[index].checked && !containerContracts[index].found"
                class="contract-status contract-status--warn">
                <v-icon small class="mr-1">mdi-alert-outline</v-icon>
                No contract found for this customer and lane.
              </div>
            </div>
          </div>
        </div>

        <!-- Execution Assignment Section -->
        <div class="section-block mt-4">
          <div class="section-header d-flex align-center mb-3">
            <v-icon small color="primary" class="mr-1"
              >mdi-truck-outline</v-icon
            >
            <span
              class="text-caption font-weight-bold text-uppercase primary--text"
            >
              Execution Assignment
            </span>
          </div>

          <v-checkbox
            v-model="form.is_sub_hire"
            label="Is this a Sub-Hire execution?"
            hide-details="auto"
            dense
            class="mt-0 mb-3"
            @change="onSubHireChange"
          />

          <v-row dense v-if="form.is_sub_hire">
            <v-col cols="12">
              <Autocomplete
                label="Select Vendor *"
                :items="vendorList"
                itemText="company_name"
                itemValue="id"
                :loading="vendorLoading"
                :totalItems="vendorCount"
                @updateList="fetchVendors"
                hide-details="auto"
                v-model="form.vendor"
                @input="onVendorChange"
              />
            </v-col>
          </v-row>

          <v-row dense v-else>
            <v-col cols="6">
              <Autocomplete
                label="Vehicle Registration"
                :items="vehicleList"
                itemText="vehicle_number"
                itemValue="id"
                :loading="vehicleLoading"
                :totalItems="vehicleCount"
                @updateList="fetchVehicles"
                hide-details="auto"
                v-model="form.vehicle"
                @input="onVehicleChange"
              />
            </v-col>
            <v-col cols="6">
              <Autocomplete
                label="Driver Name"
                :items="driverList"
                itemText="driver_name"
                itemValue="id"
                :loading="driverLoading"
                :totalItems="driverCount"
                @updateList="fetchDrivers"
                hide-details="auto"
                v-model="form.driver"
                @input="onDriverChange"
              />
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!isFormValid || !contractsValid"
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-check-circle</v-icon>
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  components: { Autocomplete, MultiSelect },
  props: {
    value: { type: Boolean, default: false },
    orderId: { type: Number, default: null },
    sourceLocation: { type: Number, default: null },
    orderInfo: { type: Object, default: () => ({}) },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData();
      }
    },
  },
  data() {
    return {
      form: {
        customer: null,
        lane: null,
        is_sub_hire: false,
        vendor: null,
        vehicle: null,
        vehicle_number: null,
        driver: null,
        driver_name: null,
        containers: [{ container_no: "", container_type: null, customer: null, lane: null }],
      },
      // per-container state
      laneLists: [],        // laneLists[i] = lane options for container i (falls back to laneList)
      containerContracts: [], // containerContracts[i] = { checked, found, contractId, rate }
      hasContract: false,
      contractChecked: false,
      contractChecking: false,
      contractIdDisplay: null,
      contractRates: [],
      hasVendorContract: false,
      vendorContractIdDisplay: null,
      vendorRates: [],
      customerList: [],
      customerCount: 0,
      customerLoading: false,
      laneList: [],
      laneCount: 0,
      laneLoading: false,
      containerTypeList: [],
      vehicleList: [],
      vehicleCount: 0,
      vehicleLoading: false,
      driverList: [],
      driverCount: 0,
      driverLoading: false,
      vendorList: [],
      vendorCount: 0,
      vendorLoading: false,
      submitting: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    filledContainers() {
      return this.form.containers.filter((c) => c.container_no && c.container_no.trim());
    },
    isFormValid() {
      if (!this.filledContainers.length) return false;
      // every filled container must have a customer
      const allHaveCustomer = this.filledContainers.every((c) => {
        const cust = c.customer || this.form.customer;
        return !!(cust && (typeof cust === 'object' ? cust.id : cust));
      });
      if (!allHaveCustomer) return false;
      if (this.form.is_sub_hire) return !!this.form.vendor;
      return !!this.form.vehicle && !!this.form.driver;
    },
    contractsValid() {
      if (this.form.is_sub_hire && !this.hasVendorContract) return false;
      // at least one container must have a checked+found contract
      if (!this.containerContracts.length) return false;
      const anyFound = this.containerContracts.some((cc) => cc && cc.checked && cc.found);
      return anyFound;
    },
  },
  methods: {
    _normVeh(v) {
      const s = (v || "").replace(/\s+/g, "").toUpperCase();
      return s.replace(/\d/g, "") + s.replace(/\D/g, "");
    },

    prefillContainers() {
      const raw = this.orderInfo?.containerData;
      if (!Array.isArray(raw) || !raw.length) return;

      // build a map from container_no → lane info from daLaneList
      const daLaneList = this.orderInfo?.daLaneList || [];
      const laneByContainer = {};
      daLaneList.forEach((dl) => {
        (dl.containers || []).forEach((cno) => {
          laneByContainer[(cno || '').toUpperCase()] = dl;
        });
      });

      this.form.containers = raw.slice(0, 4).map((c) => {
        const cno = (c.container_no || c.container_number || '').toUpperCase();
        const dlInfo = laneByContainer[cno];
        return {
          container_no: c.container_no || c.container_number || '',
          container_type: c.container_type || null,
          _typeNameHint: c.type_name || c.container_type_name || null,
          customer: null,
          lane: dlInfo ? { id: dlInfo.lane_id, lane_title: dlInfo.lane_title } : null,
          _prefillLaneId: dlInfo ? dlInfo.lane_id : null,
        };
      });

      // seed laneLists so each container shows its prefilled lane option
      this.laneLists = this.form.containers.map((c) => {
        if (c.lane) return [c.lane];
        return [];
      });
      this.containerContracts = this.form.containers.map(() => null);
    },

    matchContainerTypes() {
      if (!this.containerTypeList.length) return;
      this.form.containers = this.form.containers.map((c) => {
        // if id already set, keep it; otherwise match by name hint
        if (!c.container_type && c._typeNameHint) {
          const match = this.containerTypeList.find(
            (t) => t.type_name === c._typeNameHint,
          );
          return { ...c, container_type: match ? match.id : null };
        }
        return c;
      });
    },

    prefillVehicle() {
      if (this.form.vehicle) return; // already set
      const num = this.orderInfo?.vehicleNumber;
      if (!num) return;
      const normTarget = this._normVeh(num);
      const match = this.vehicleList.find(
        (v) =>
          this._normVeh(v.vehicle_number) === normTarget ||
          this._normVeh(v.inventory_number) === normTarget,
      );
      if (match) {
        this.form.vehicle = { ...match };
        this.form.vehicle_number =
          match.vehicle_number || match.inventory_number;
      } else {
        // not in first page — search by the raw number
        this.$api.vehicle
          .getVehicleSelectList({ search: num, limit: 1 })
          .then((res) => {
            if (res.data && res.data.length) {
              const v = res.data[0];
              if (!this.vehicleList.find((x) => x.id === v.id))
                this.vehicleList.push(v);
              this.form.vehicle = { ...v };
              this.form.vehicle_number = v.vehicle_number || v.inventory_number;
            }
          });
      }
    },

    prefillDriver() {
      if (this.form.driver) return; // already set
      const name = this.orderInfo?.driverName;
      if (!name) return;
      const match = this.driverList.find((d) => d.driver_name === name);
      if (match) {
        this.form.driver = { ...match };
        this.form.driver_name = match.driver_name;
      } else {
        // not in first page — search by name
        this.$api.driver
          .getDriverSelectList({ search: name, limit: 1 })
          .then((res) => {
            if (res.data && res.data.length) {
              const d = res.data[0];
              if (!this.driverList.find((x) => x.id === d.id))
                this.driverList.push(d);
              this.form.driver = { ...d };
              this.form.driver_name = d.driver_name;
            }
          });
      }
    },

    resetForm() {
      this.form = {
        customer: null,
        lane: null,
        is_sub_hire: false,
        vendor: null,
        vehicle: null,
        vehicle_number: null,
        driver: null,
        driver_name: null,
        containers: [{ container_no: "", container_type: null, customer: null, lane: null }],
      };
      this.laneLists = [];
      this.containerContracts = [];
      this.hasContract = false;
      this.contractChecked = false;
      this.contractIdDisplay = null;
      this.contractRates = [];
      this.hasVendorContract = false;
      this.vendorContractIdDisplay = null;
      this.vendorRates = [];
      this.customerList = [];
      this.laneList = [];
      this.vehicleList = [];
      this.driverList = [];
      this.vendorList = [];
    },

    closeDialog() {
      this.showDialog = false;
    },

    addContainer() {
      if (this.form.containers.length < 4) {
        this.form.containers.push({ container_no: "", container_type: null, customer: null, lane: null });
        this.laneLists.push([...this.laneList]);
        this.containerContracts.push(null);
      }
    },

    removeContainer(index) {
      this.form.containers.splice(index, 1);
      this.laneLists.splice(index, 1);
      this.containerContracts.splice(index, 1);
    },

    onSubHireChange() {
      this.form.vendor = null;
      this.form.vehicle = null;
      this.form.vehicle_number = null;
      this.form.driver = null;
      this.form.driver_name = null;
      this._resetContract();
      if (this.form.is_sub_hire) {
        this.vendorList = [];
        this.fetchVendors({ limit: 10, offset: 0 });
      } else {
        this.checkContract();
      }
    },

    onVehicleChange(item) {
      if (!item) {
        this.form.vehicle_number = null;
        return;
      }
      this.form.vehicle_number = item.vehicle_number || item.inventory_number;
      const vehicleId = typeof item === "object" ? item.id : item;
      const driver = this.driverList.find(
        (d) => d.assigned_vehicle_id === vehicleId,
      );
      if (driver) {
        this.form.driver = { ...driver };
      } else {
        // assigned driver may not be in the first page — fetch by assigned_vehicle filter
        this.$api.driver
          .getDriverSelectList({ assigned_vehicle: vehicleId, limit: 1 })
          .then((res) => {
            if (res.data && res.data.length) {
              const d = res.data[0];
              if (!this.driverList.find((x) => x.id === d.id))
                this.driverList.push(d);
              this.form.driver = { ...d };
              this.form.driver_name = d.driver_name;
            }
          });
      }
    },

    onDriverChange(item) {
      if (!item) {
        this.form.driver_name = null;
        return;
      }
      this.form.driver_name = item.driver_name;
      if (!item.assigned_vehicle_id) return;
      const vehicle = this.vehicleList.find(
        (v) => v.id === item.assigned_vehicle_id,
      );
      if (vehicle) {
        this.form.vehicle = { ...vehicle };
        this.form.vehicle_number =
          vehicle.vehicle_number || vehicle.inventory_number;
      } else {
        // assigned vehicle may not be in the first page — fetch directly by id
        this.$api.vehicle
          .getVehicleObject(item.assigned_vehicle_id)
          .then((res) => {
            const v = res.data || res;
            if (v && v.id) {
              const entry = {
                id: v.id,
                vehicle_number: v.vehicle_number,
                inventory_number: v.inventory_number,
              };
              if (!this.vehicleList.find((x) => x.id === entry.id))
                this.vehicleList.push(entry);
              this.form.vehicle = { ...entry };
              this.form.vehicle_number =
                entry.vehicle_number || entry.inventory_number;
            }
          });
      }
    },

    onContractFieldChange() {
      this._resetContract();
      this.checkContract();
    },

    onCustomerChange() {
      this._resetContract();
      this.checkContract();
    },

    onLaneChange() {
      this._resetContract();
      if (!this.form.customer) {
        this._resolveCustomerFromLane();
      } else {
        this.checkContract();
      }
    },

    _resolveCustomerFromLane() {
      const laneId =
        this.form.lane && typeof this.form.lane === "object"
          ? this.form.lane.id
          : this.form.lane;
      if (!laneId) return;

      const containerTypeId =
        this.form.containers.map((c) => c.container_type).filter(Boolean)[0] ||
        null;
      const params = { lane_id: laneId };
      if (containerTypeId) params.container_type_id = containerTypeId;

      this.$api.emergencyOrder
        .resolveCustomerByLane(params)
        .then((res) => {
          const data = res.data || res;
          if (data.customer_id) {
            const existing = this.customerList.find(
              (c) => c.id === data.customer_id,
            );
            if (existing) {
              this.form.customer = existing;
            } else {
              const newCustomer = {
                id: data.customer_id,
                company_name: data.company_name,
              };
              this.customerList = [newCustomer, ...this.customerList];
              this.form.customer = newCustomer;
            }
            this.checkContract();
          }
        })
        .catch(() => {});
    },

    onVendorChange() {
      this._resetContract();
      this.checkContract();
    },

    onContainerCustomerChange(index, val) {
      this.$set(this.form.containers, index, { ...this.form.containers[index], customer: val });
      // also update form.customer if it's the first container (fallback)
      if (index === 0) this.form.customer = val;
      this._checkContainerContract(index);
    },

    onContainerLaneChange(index, val) {
      this.$set(this.form.containers, index, { ...this.form.containers[index], lane: val });
      if (index === 0) this.form.lane = val;
      // ensure the selected lane is in laneLists[index] for re-render
      if (val) {
        const list = this.laneLists[index] || [...this.laneList];
        const id = typeof val === 'object' ? val.id : val;
        if (!list.find((l) => l.id === id)) list.unshift(typeof val === 'object' ? val : { id: val });
        this.$set(this.laneLists, index, list);
      }
      this._checkContainerContract(index);
    },

    _checkContainerContract(index) {
      const container = this.form.containers[index];
      const custRaw = container.customer || this.form.customer;
      const laneRaw = container.lane || this.form.lane;
      const customerId = custRaw && typeof custRaw === 'object' ? custRaw.id : custRaw;
      const laneId = laneRaw && typeof laneRaw === 'object' ? laneRaw.id : laneRaw;
      if (!customerId || !laneId) return;

      this.$set(this.containerContracts, index, { checked: false, found: false });

      const params = { customer_id: customerId, lane_id: laneId };
      if (container.container_type) params.container_type_id = container.container_type;

      this.$api.emergencyOrder
        .checkEmergencyContract(params)
        .then((res) => {
          const data = res.data || res;
          this.$set(this.containerContracts, index, {
            checked: true,
            found: !!data.has_customer_contract,
            contractId: data.contract_id_display || null,
            rate: data.contract_rates && data.contract_rates.length ? data.contract_rates[0].rate : null,
          });
          // also keep legacy top-level flags from first container for vendor contract check
          if (index === 0) {
            this.hasContract = data.has_customer_contract;
            this.contractChecked = true;
            this.contractIdDisplay = data.contract_id_display || null;
            this.contractRates = data.contract_rates || [];
            this.hasVendorContract = data.has_vendor_contract || false;
            this.vendorContractIdDisplay = data.vendor_contract_id_display || null;
            this.vendorRates = data.vendor_rates || [];
          }
        })
        .catch(() => {});
    },

    _resetContract() {
      this.contractChecked = false;
      this.hasContract = false;
      this.contractIdDisplay = null;
      this.contractRates = [];
      this.hasVendorContract = false;
      this.vendorContractIdDisplay = null;
      this.vendorRates = [];
    },

    checkContract() {
      const customerId =
        this.form.customer && typeof this.form.customer === "object"
          ? this.form.customer.id
          : this.form.customer;
      const laneId =
        this.form.lane && typeof this.form.lane === "object"
          ? this.form.lane.id
          : this.form.lane;
      if (!customerId || !laneId) return;

      const vendorId = this.form.is_sub_hire
        ? this.form.vendor && typeof this.form.vendor === "object"
          ? this.form.vendor.id
          : this.form.vendor
        : null;

      // For sub-hire, also require vendor before checking
      if (this.form.is_sub_hire && !vendorId) return;

      this.contractChecking = true;
      const params = { customer_id: customerId, lane_id: laneId };
      const containerTypeIds = [
        ...new Set(
          this.form.containers.map((c) => c.container_type).filter(Boolean),
        ),
      ];
      if (containerTypeIds.length)
        params.container_type_id = containerTypeIds.join(",");
      if (vendorId) params.vendor_id = vendorId;

      this.$api.emergencyOrder
        .checkEmergencyContract(params)
        .then((res) => {
          const data = res.data || res;
          this.hasContract = data.has_customer_contract;
          this.contractIdDisplay = data.contract_id_display || null;
          this.contractRates = data.contract_rates || [];
          this.hasVendorContract = data.has_vendor_contract || false;
          this.vendorContractIdDisplay =
            data.vendor_contract_id_display || null;
          this.vendorRates = data.vendor_rates || [];
          this.contractChecked = true;
        })
        .catch(() => {})
        .finally(() => {
          this.contractChecking = false;
        });
    },

    fetchInitialData() {
      this.prefillContainers();
      // ensure arrays are sized for current containers
      while (this.laneLists.length < this.form.containers.length) this.laneLists.push([]);
      while (this.containerContracts.length < this.form.containers.length) this.containerContracts.push(null);
      this.fetchCustomers({ limit: 10, offset: 0 });
      this.fetchLanes({ limit: 10, offset: 0 });
      this.fetchContainerTypes();
      this.fetchVehicles({ limit: 10, offset: 0 });
      this.fetchDrivers({ limit: 10, offset: 0 });
      if (this.orderInfo?.vendorId) {
        this.form.is_sub_hire = true;
        this.fetchVendors({ limit: 10, offset: 0 });
      }
    },

    fetchCustomers(params = {}) {
      this.customerLoading = true;
      const query = {
        company_type: "Customer",
        is_emergency_customer: true,
        limit: 10,
        ...params,
      };
      this.$api.company
        .getCompanyList(query)
        .then((res) => {
          this.customerList = params.offset
            ? [...this.customerList, ...res.data]
            : res.data;
          this.customerCount = res.count;
          if (
            !params.offset &&
            this.orderInfo?.customerId &&
            !this.form.customer
          ) {
            const match = this.customerList.find(
              (c) => c.id === this.orderInfo.customerId,
            );
            if (match) {
              this.form.customer = match;
              this.checkContract();
            } else {
              this.$api.company
                .getCompanyObject(this.orderInfo.customerId)
                .then((r) => {
                  if (r.data && r.data.id) {
                    this.customerList = [r.data, ...this.customerList];
                    this.form.customer = r.data;
                    this.checkContract();
                  }
                });
            }
          }
        })
        .finally(() => {
          this.customerLoading = false;
        });
    },

    fetchLanes(params = {}) {
      this.laneLoading = true;
      const query = { limit: 10, ...params };
      if (this.sourceLocation) query.source_location = this.sourceLocation;
      this.$api.lane
        .getLaneList(query)
        .then((res) => {
          this.laneList = params.offset
            ? [...this.laneList, ...res.data]
            : res.data;
          this.laneCount = res.count;
          // seed laneLists for containers that don't have their own list yet
          this.form.containers.forEach((c, i) => {
            if (!this.laneLists[i] || !this.laneLists[i].length) {
              const base = [...this.laneList];
              // prepend prefilled lane if not already in list
              if (c.lane) {
                const id = typeof c.lane === 'object' ? c.lane.id : c.lane;
                if (!base.find((l) => l.id === id)) base.unshift(c.lane);
              }
              this.$set(this.laneLists, i, base);
            }
          });
        })
        .finally(() => {
          this.laneLoading = false;
        });
    },

    fetchContainerTypes() {
      this.$api.containerType
        .getContainerTypeList({ limit: "all" })
        .then((res) => {
          this.containerTypeList = res.data;
          this.matchContainerTypes();
        });
    },

    fetchVehicles(params = {}) {
      this.vehicleLoading = true;
      this.$api.vehicle
        .getVehicleSelectList({ limit: 10, ...params })
        .then((res) => {
          this.vehicleList = params.offset
            ? [...this.vehicleList, ...res.data]
            : res.data;
          this.vehicleCount = res.count;
          this.prefillVehicle();
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },

    fetchDrivers(params = {}) {
      this.driverLoading = true;
      this.$api.driver
        .getDriverSelectList({ limit: 10, ...params })
        .then((res) => {
          this.driverList = params.offset
            ? [...this.driverList, ...res.data]
            : res.data;
          this.driverCount = res.count;
          this.prefillDriver();
        })
        .finally(() => {
          this.driverLoading = false;
        });
    },

    fetchVendors(params = {}) {
      this.vendorLoading = true;
      this.$api.company
        .getCompanyList({ company_type: "Transporter", limit: 10, ...params })
        .then((res) => {
          const vendors = Array.isArray(res.data) ? res.data : [];
          this.vendorList = params.offset
            ? [...this.vendorList, ...vendors]
            : vendors;
          this.vendorCount = res.count || 0;
          if (!params.offset && this.orderInfo?.vendorId && !this.form.vendor) {
            const match = this.vendorList.find((v) => v.id === this.orderInfo.vendorId);
            if (match) {
              this.form.vendor = match;
              this.checkContract();
            } else {
              this.$api.company.getCompanyObject(this.orderInfo.vendorId).then((r) => {
                if (r.data && r.data.id) {
                  this.vendorList = [r.data, ...this.vendorList];
                  this.form.vendor = r.data;
                  this.checkContract();
                }
              });
            }
          }
        })
        .catch(() => {
          this.vendorList = [];
        })
        .finally(() => {
          this.vendorLoading = false;
        });
    },

    submit() {
      this.submitting = true;
      const getVal = (v) => (v && typeof v === "object" ? v.id : v);
      const containers = this.form.containers
        .filter((c) => c.container_no || c.container_type)
        .map((c) => ({
          container_no: c.container_no,
          container_type: c.container_type,
          customer: getVal(c.customer) || getVal(this.form.customer),
          lane: getVal(c.lane) || getVal(this.form.lane),
        }));

      // fallback top-level customer/lane from first container
      const firstCustomer = containers[0]?.customer || getVal(this.form.customer);
      const firstLane = containers[0]?.lane || getVal(this.form.lane);

      const payload = {
        customer: firstCustomer,
        lane: firstLane,
        is_sub_hire: this.form.is_sub_hire,
        vendor: this.form.is_sub_hire ? getVal(this.form.vendor) : null,
        vehicle: !this.form.is_sub_hire ? getVal(this.form.vehicle) : null,
        vehicle_number: this.form.vehicle_number,
        driver: !this.form.is_sub_hire ? getVal(this.form.driver) : null,
        driver_name: this.form.driver_name,
        containers,
      };
      this.$api.emergencyOrder
        .confirmDA(this.orderId, payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "DA confirmed successfully!",
            color: "success",
          });
          this.$emit("daConfirmed");
          this.closeDialog();
        })
        .catch(console.error)
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.section-block {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  border: 1px solid #e9ecef;
}
.section-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}
.container-row {
  background: white;
  border-radius: 6px;
  padding: 6px 8px;
  border: 1px solid #eeeeee;
}
.info-item {
  font-size: 12px;
}
.info-label {
  color: #888;
  font-weight: 400;
}
.info-value {
  color: #2c3e50;
  font-weight: 600;
}
.contract-status {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}
.contract-status--warn {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #795548;
}
.contract-status--found {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #2e7d32;
}
.rate-chip {
  background: #e3f2fd;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  color: #1565c0;
}
.rate-chip--missing {
  background: #fce4ec;
  color: #c62828;
}
</style>
